/**
 * Built-in sticker pack — bundled same-origin SVG art (NFR-PWA-002: no remote assets).
 * Sends as a tiny inline E2EE reference (§9.5): both ends render from the local pack,
 * so no sticker bytes ever travel and no provider is involved (§1.2 minimal metadata).
 * Animated stickers use SMIL, which animates inside <img> without scripts.
 */

import { STICKER_GIF_FEATURE_STATUS } from "@veto/protocol";

export const STICKER_PACK_ID = "veto.classic";
export const STICKER_PACK_DOODLE = "veto.doodle";

export interface StickerPack {
  id: string;
  label: string;
  note: string;
  stickers: BuiltinSticker[];
}

export interface BuiltinSticker {
  id: string;
  animated: boolean;
  keywords: string;
  uri: string;
}

const P = {
  iris: "#6C5CFF",
  irisDeep: "#4B3BD6",
  irisBright: "#8B7CFF",
  irisSoft: "#DCD6FF",
  cyan: "#3FD8E4",
  navy: "#0D1322",
  panel: "#16203A",
  ink: "#EDF1FC",
  amber: "#F6B852",
  pink: "#F4586E",
  green: "#3DDC97",
};

function svgDoc(body: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 112 112">${body}</svg>`;
}

function dataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function gradDef(id: string, from: string, to: string): string {
  return `<defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/></linearGradient></defs>`;
}

function happyFace(eye = "#0D1322"): string {
  return `<circle cx="40" cy="48" r="6" fill="${eye}"/><circle cx="72" cy="48" r="6" fill="${eye}"/><path d="M38 66 Q56 84 74 66" stroke="${eye}" stroke-width="6" stroke-linecap="round" fill="none"/>`;
}

function heartPath(cx: number, cy: number, s: number, fill: string, extra = ""): string {
  return `<path transform="translate(${cx} ${cy}) scale(${s})" d="M0 14 C-3 6 -22 -6 -14 -16 C-8 -23 0 -18 0 -12 C0 -18 8 -23 14 -16 C22 -6 3 6 0 14 Z" fill="${fill}" ${extra}/>`;
}

interface Def {
  id: string;
  body: string;
  animated?: boolean;
  keywords: string;
}

const CLASSIC_DEFS: Def[] = [
  {
    id: "thumbs-up",
    keywords: "thumbs up like good yes approve",
    body: `${gradDef("g", P.iris, P.irisDeep)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M34 58 h14 v26 h-14 a6 6 0 0 1 -6 -6 v-14 a6 6 0 0 1 6 -6 Z" fill="${P.ink}"/>
      <path d="M48 84 v-24 c0 -3 1.6 -5 4 -7 l10 -9 c2.4 -2 3 -6 1.6 -9.4 c-1 -2.6 -4.4 -3 -6 -0.6 l-5 7 c-1.6 2.2 -4.6 2 -6.4 0.4 v-12 c0 -3.4 -3.6 -5.6 -6.6 -4 l-14 8 c-2 1.2 -3.6 3.6 -3.6 6 v22" fill="${P.ink}" stroke="none"/>
      <path d="M48 60 l14 -13 c3 -2.8 8 -1.6 9 2.6 l3 16 c0.8 4 -2 7.4 -6 7.4 h-20" fill="none" stroke="${P.ink}" stroke-width="6" stroke-linejoin="round"/>
      <path d="M48 34 c-3.4 -2 -8 -1 -9.6 2.8 c-1 2.6 0.2 5.6 2.6 7 l7 4" fill="${P.ink}"/>`,
  },
  {
    id: "heart",
    keywords: "heart love red valentine",
    body: `${gradDef("g", P.pink, "#B8324F")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      ${heartPath(56, 58, 2.4, P.ink)}
      <circle cx="42" cy="46" r="4" fill="rgba(255,255,255,0.5)"/>`,
  },
  {
    id: "lol",
    keywords: "laughing lol joy funny rofl",
    body: `${gradDef("g", P.amber, "#D98324")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M30 44 q8 -8 16 0" stroke="${P.navy}" stroke-width="5" stroke-linecap="round" fill="none"/>
      <path d="M66 44 q8 -8 16 0" stroke="${P.navy}" stroke-width="5" stroke-linecap="round" fill="none"/>
      <path d="M34 60 Q56 88 78 60 Z" fill="${P.navy}"/>
      <path d="M40 64 Q56 78 72 64 Q56 70 40 64 Z" fill="${P.pink}"/>
      <path d="M22 52 q-6 10 0 16 q8 -4 6 -16 Z" fill="${P.cyan}"/>
      <path d="M90 52 q6 10 0 16 q-8 -4 -6 -16 Z" fill="${P.cyan}"/>`,
  },
  {
    id: "cool",
    keywords: "cool sunglasses chill nice deal with it",
    body: `${gradDef("g", P.cyan, "#1B9AA6")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M24 44 h64 l-2 6 c-2 10 -10 16 -20 16 h-6 c-4 0 -6 -2 -8 -4 c-2 2 -4 4 -8 4 h-6 c-10 0 -18 -6 -20 -16 Z" fill="${P.navy}"/>
      <path d="M28 47 c10 2 18 2 24 1 M60 48 c8 1 16 1 24 -1" stroke="${P.cyan}" stroke-width="3" stroke-linecap="round" fill="none"/>
      <path d="M42 74 Q56 86 70 74" stroke="${P.navy}" stroke-width="6" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "love-eyes",
    keywords: "heart eyes love crush adore",
    body: `${gradDef("g", P.irisBright, P.irisDeep)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      ${heartPath(40, 46, 1.15, P.pink)}
      ${heartPath(72, 46, 1.15, P.pink)}
      <path d="M40 68 Q56 84 72 68" stroke="${P.navy}" stroke-width="6" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "fire",
    keywords: "fire lit hot flame burn lit",
    body: `${gradDef("g", P.panel, P.navy)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M56 20 C70 36 82 44 82 62 a26 26 0 0 1 -52 0 c0 -14 10 -20 16 -30 c2 8 6 10 10 12 c-2 -10 0 -18 0 -24 Z" fill="${P.amber}"/>
      <path d="M56 46 c8 8 14 12 14 22 a14 14 0 0 1 -28 0 c0 -8 6 -12 14 -22 Z" fill="${P.pink}"/>
      <path d="M56 62 c4 5 6 8 6 13 a6 6 0 0 1 -12 0 c0 -5 2 -8 6 -13 Z" fill="${P.ink}"/>`,
  },
  {
    id: "party",
    keywords: "party popper celebrate congrats tada birthday",
    body: `${gradDef("g", P.iris, "#9340C8")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M30 82 L52 44 a8 8 0 0 1 14 8 L40 88 a6 6 0 0 1 -10 -6 Z" fill="${P.amber}"/>
      <path d="M34 76 l12 6 M38 68 l10 5" stroke="${P.navy}" stroke-width="4" stroke-linecap="round"/>
      <circle cx="70" cy="30" r="4" fill="${P.pink}"/>
      <circle cx="84" cy="44" r="4" fill="${P.cyan}"/>
      <rect x="62" y="46" width="7" height="7" rx="2" fill="${P.green}" transform="rotate(20 65 49)"/>
      <rect x="78" y="26" width="6" height="6" rx="2" fill="${P.ink}" transform="rotate(-15 81 29)"/>
      <path d="M66 20 q6 -6 12 0 M84 58 q6 4 10 0" stroke="${P.ink}" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "shield-check",
    keywords: "veto shield verified secure encrypted protected",
    body: `${gradDef("g", P.cyan, P.iris)}
      <path d="M56 12 L94 26 v26 c0 22 -16 38 -38 48 C34 90 18 74 18 52 V26 Z" fill="url(#g)"/>
      <path d="M56 22 L86 33 v19 c0 17 -12 30 -30 38 C38 82 26 69 26 52 V33 Z" fill="${P.navy}" opacity="0.35"/>
      <path d="M38 56 l12 12 l24 -26" stroke="${P.ink}" stroke-width="9" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  },
  {
    id: "rocket",
    keywords: "rocket launch fast ship space startup",
    body: `${gradDef("g", P.panel, "#1E2C4E")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M56 18 c12 8 18 24 18 40 l-8 10 h-20 l-8 -10 c0 -16 6 -32 18 -40 Z" fill="${P.ink}"/>
      <circle cx="56" cy="46" r="8" fill="${P.cyan}" stroke="${P.irisDeep}" stroke-width="3"/>
      <path d="M46 62 l-10 12 c6 2 8 6 8 10 Z M66 62 l10 12 c-6 2 -8 6 -8 10 Z" fill="${P.pink}"/>
      <path d="M50 74 h12 c0 8 -3 14 -6 20 c-3 -6 -6 -12 -6 -20 Z" fill="${P.amber}"/>`,
  },
  {
    id: "ghost",
    keywords: "ghost boo spooky halloween cute",
    body: `${gradDef("g", P.panel, "#232F52")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M32 88 v-30 a24 24 0 0 1 48 0 v30 l-8 -7 -8 7 -8 -7 -8 7 -8 -7 Z" fill="${P.ink}"/>
      <circle cx="47" cy="52" r="5" fill="${P.navy}"/>
      <circle cx="65" cy="52" r="5" fill="${P.navy}"/>
      <ellipse cx="56" cy="66" rx="5" ry="6" fill="${P.navy}"/>
      <circle cx="38" cy="30" r="3" fill="${P.cyan}"/>
      <circle cx="76" cy="24" r="2.5" fill="${P.irisBright}"/>`,
  },
  {
    id: "pizza",
    keywords: "pizza slice food italian cheese",
    body: `${gradDef("g", P.amber, "#C97B1E")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M56 22 L86 78 a34 10 0 0 1 -60 0 Z" fill="${P.ink}"/>
      <path d="M56 30 L80 74 a28 8 0 0 1 -48 0 Z" fill="${P.amber}"/>
      <circle cx="56" cy="52" r="5" fill="${P.pink}"/>
      <circle cx="46" cy="64" r="4.5" fill="${P.pink}"/>
      <circle cx="66" cy="66" r="4.5" fill="${P.pink}"/>
      <circle cx="56" cy="72" r="4" fill="${P.green}"/>
      <path d="M26 78 a34 10 0 0 0 60 0" fill="none" stroke="${P.pink}" stroke-width="5"/>`,
  },
  {
    id: "coffee",
    keywords: "coffee cup tea cafe morning caffeine",
    body: `${gradDef("g", "#8A5A2B", "#4E2F14")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <rect x="30" y="46" width="40" height="30" rx="8" fill="${P.ink}"/>
      <path d="M70 50 h6 a8 8 0 0 1 0 16 h-6" fill="none" stroke="${P.ink}" stroke-width="6"/>
      <rect x="30" y="46" width="40" height="8" rx="4" fill="${P.navy}"/>
      <path d="M40 38 q3 -6 0 -10 M52 38 q3 -6 0 -10 M64 38 q3 -6 0 -10" stroke="${P.irisSoft}" stroke-width="4" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "star",
    keywords: "star favorite gold winner amazing",
    body: `${gradDef("g", P.amber, "#C97B1E")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M56 24 l8.4 17.2 19 2.8 -13.7 13.4 3.2 18.8 -16.9 -8.9 -16.9 8.9 3.2 -18.8 -13.7 -13.4 19 -2.8 Z" fill="${P.ink}" stroke="${P.navy}" stroke-width="3" stroke-linejoin="round"/>
      <circle cx="50" cy="42" r="3.5" fill="rgba(255,255,255,0.7)"/>`,
  },
  {
    id: "crown",
    keywords: "crown king queen royal winner best",
    body: `${gradDef("g", P.irisBright, P.irisDeep)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M26 70 l-4 -28 16 12 18 -22 18 22 16 -12 -4 28 a6 6 0 0 1 -6 5 h-48 a6 6 0 0 1 -6 -5 Z" fill="${P.amber}" stroke="${P.navy}" stroke-width="3" stroke-linejoin="round"/>
      <circle cx="26" cy="38" r="5" fill="${P.ink}"/>
      <circle cx="56" cy="28" r="5" fill="${P.ink}"/>
      <circle cx="86" cy="38" r="5" fill="${P.ink}"/>
      <rect x="30" y="74" width="52" height="7" rx="3.5" fill="${P.pink}" stroke="${P.navy}" stroke-width="2.5"/>`,
  },
  {
    id: "panda",
    keywords: "panda cute animal china bear",
    body: `${gradDef("g", "#3E8E5A", "#1C5937")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <circle cx="34" cy="34" r="12" fill="${P.navy}"/>
      <circle cx="78" cy="34" r="12" fill="${P.navy}"/>
      <ellipse cx="56" cy="58" rx="30" ry="27" fill="${P.ink}"/>
      <ellipse cx="42" cy="52" rx="9" ry="11" fill="${P.navy}" transform="rotate(-15 42 52)"/>
      <ellipse cx="70" cy="52" rx="9" ry="11" fill="${P.navy}" transform="rotate(15 70 52)"/>
      <circle cx="43" cy="54" r="3.4" fill="${P.ink}"/>
      <circle cx="69" cy="54" r="3.4" fill="${P.ink}"/>
      <ellipse cx="56" cy="64" rx="4.5" ry="3.5" fill="${P.navy}"/>
      <path d="M50 70 q6 5 12 0" stroke="${P.navy}" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "cat",
    keywords: "cat kitten meow pet cute animal",
    body: `${gradDef("g", P.iris, P.irisDeep)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M32 40 l-2 -16 16 9 Z" fill="${P.ink}"/>
      <path d="M80 40 l2 -16 -16 9 Z" fill="${P.ink}"/>
      <ellipse cx="56" cy="60" rx="28" ry="25" fill="${P.ink}"/>
      <path d="M34 44 l14 4 M78 44 l-14 4" stroke="${P.navy}" stroke-width="2.6" stroke-linecap="round"/>
      <circle cx="46" cy="56" r="4" fill="${P.green}"/>
      <circle cx="66" cy="56" r="4" fill="${P.green}"/>
      <path d="M56 63 l-3.5 4 h7 Z" fill="${P.pink}"/>
      <path d="M48 70 q8 7 16 0" stroke="${P.navy}" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "dog",
    keywords: "dog puppy woof pet cute animal good boy",
    body: `${gradDef("g", P.amber, "#B3701D")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <ellipse cx="30" cy="56" rx="9" ry="16" fill="${P.navy}" transform="rotate(-12 30 56)"/>
      <ellipse cx="82" cy="56" rx="9" ry="16" fill="${P.navy}" transform="rotate(12 82 56)"/>
      <ellipse cx="56" cy="58" rx="27" ry="25" fill="${P.ink}"/>
      <circle cx="45" cy="52" r="4.5" fill="${P.navy}"/>
      <circle cx="67" cy="52" r="4.5" fill="${P.navy}"/>
      <ellipse cx="56" cy="64" rx="6" ry="5" fill="${P.navy}"/>
      <path d="M56 69 v4 M56 73 q-7 5 -12 0 M56 73 q7 5 12 0" stroke="${P.navy}" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "flower",
    keywords: "flower bloom spring pretty nature",
    body: `${gradDef("g", "#4E9C77", "#1F5C43")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <g fill="${P.irisBright}">
        <ellipse cx="56" cy="32" rx="10" ry="15"/>
        <ellipse cx="56" cy="80" rx="10" ry="15"/>
        <ellipse cx="32" cy="56" rx="15" ry="10"/>
        <ellipse cx="80" cy="56" rx="15" ry="10"/>
        <ellipse cx="39" cy="39" rx="10" ry="14" transform="rotate(-45 39 39)"/>
        <ellipse cx="73" cy="39" rx="10" ry="14" transform="rotate(45 73 39)"/>
        <ellipse cx="39" cy="73" rx="10" ry="14" transform="rotate(45 39 73)"/>
        <ellipse cx="73" cy="73" rx="10" ry="14" transform="rotate(-45 73 73)"/>
      </g>
      <circle cx="56" cy="56" r="11" fill="${P.amber}" stroke="${P.navy}" stroke-width="3"/>
      <path d="M44 88 q12 8 24 0" stroke="${P.green}" stroke-width="4" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "sun",
    keywords: "sun sunny bright day hot summer",
    body: `${gradDef("g", P.cyan, "#1B8C96")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <circle cx="56" cy="56" r="22" fill="${P.amber}"/>
      <g stroke="${P.amber}" stroke-width="6" stroke-linecap="round">
        <path d="M56 20 v10 M56 82 v10 M20 56 h10 M82 56 h10"/>
        <path d="M32 32 l7 7 M73 73 l7 7 M80 32 l-7 7 M39 73 l-7 7"/>
      </g>
      <circle cx="49" cy="53" r="3" fill="${P.navy}"/>
      <circle cx="63" cy="53" r="3" fill="${P.navy}"/>
      <path d="M50 62 q6 5 12 0" stroke="${P.navy}" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "moon",
    keywords: "moon night sleep stars goodnight zzz",
    body: `${gradDef("g", "#2B3564", "#141A38")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M66 26 a30 30 0 1 0 22 40 a26 26 0 0 1 -22 -40 Z" fill="${P.amber}"/>
      <circle cx="74" cy="36" r="3" fill="${P.ink}"/>
      <circle cx="86" cy="52" r="2.4" fill="${P.ink}"/>
      <circle cx="30" cy="30" r="2" fill="${P.irisSoft}"/>
      <path d="M30 74 q4 -6 8 0 M38 70 q3 -5 6 0" stroke="${P.irisSoft}" stroke-width="2.4" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "lightning",
    keywords: "lightning bolt zap fast electric energy",
    body: `${gradDef("g", P.panel, "#1B2444")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M62 16 L34 60 h16 l-8 36 32 -48 h-17 l11 -32 Z" fill="${P.amber}" stroke="${P.navy}" stroke-width="3" stroke-linejoin="round"/>
      <circle cx="30" cy="34" r="3" fill="${P.cyan}"/>
      <circle cx="84" cy="70" r="3" fill="${P.cyan}"/>`,
  },
  {
    id: "avocado",
    keywords: "avocado healthy toast green food",
    body: `${gradDef("g", "#5FA46A", "#2A6B3C")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M56 24 c14 0 24 16 24 30 a24 26 0 0 1 -48 0 c0 -14 10 -30 24 -30 Z" fill="${P.ink}"/>
      <path d="M56 32 c10 0 17 13 17 23 a17 19 0 0 1 -34 0 c0 -10 7 -23 17 -23 Z" fill="#B7E08A"/>
      <circle cx="56" cy="58" r="9" fill="#8A5A2B"/>
      <circle cx="53" cy="55" r="3" fill="#C69758"/>`,
  },
  {
    id: "galaxy",
    keywords: "galaxy space stars universe cosmos veto",
    body: `${gradDef("g", "#241E56", "#0B0D24")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <ellipse cx="56" cy="56" rx="34" ry="14" fill="none" stroke="${P.irisBright}" stroke-width="5" transform="rotate(-24 56 56)"/>
      <ellipse cx="56" cy="56" rx="22" ry="8" fill="none" stroke="${P.cyan}" stroke-width="3.5" transform="rotate(-24 56 56)"/>
      <circle cx="56" cy="56" r="6" fill="${P.ink}"/>
      <circle cx="34" cy="34" r="2.2" fill="${P.ink}"/>
      <circle cx="80" cy="42" r="2.6" fill="${P.amber}"/>
      <circle cx="78" cy="78" r="2" fill="${P.cyan}"/>
      <circle cx="30" cy="76" r="1.8" fill="${P.irisSoft}"/>`,
  },
  {
    id: "heart-pulse",
    animated: true,
    keywords: "heartbeat pulse love alive heart animate",
    body: `${gradDef("g", P.pink, "#B8324F")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <g transform="translate(56 58)">
        <g>
          ${heartPath(0, 0, 2.1, P.ink)}
          <animateTransform attributeName="transform" type="scale" values="1;1.18;1" dur="1.1s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1"/>
        </g>
      </g>
      <path d="M28 58 h14 l6 -10 8 20 6 -10 h22" stroke="${P.cyan}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none" opacity="0.9"/>`,
  },
  {
    id: "star-spin",
    animated: true,
    keywords: "star spin shine sparkle rotate animate",
    body: `${gradDef("g", P.amber, "#C97B1E")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <g transform="translate(56 56)">
        <g>
          <path d="M0 -32 l8.4 17.2 19 2.8 -13.7 13.4 3.2 18.8 -16.9 -8.9 -16.9 8.9 3.2 -18.8 -13.7 -13.4 19 -2.8 Z" fill="${P.ink}" stroke="${P.navy}" stroke-width="3" stroke-linejoin="round"/>
          <animateTransform attributeName="transform" type="rotate" values="0;360" dur="3.2s" repeatCount="indefinite"/>
        </g>
      </g>`,
  },
  {
    id: "wave-hi",
    animated: true,
    keywords: "wave hello hi bye hand animate greeting",
    body: `${gradDef("g", P.iris, P.irisDeep)}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <g transform="translate(56 60)">
        <g>
          <g fill="${P.ink}">
            <ellipse cx="0" cy="6" rx="17" ry="15"/>
            <rect x="-19" y="-16" width="8.5" height="26" rx="4.2"/>
            <rect x="-8" y="-22" width="8.5" height="32" rx="4.2"/>
            <rect x="3" y="-20" width="8.5" height="30" rx="4.2"/>
            <rect x="13" y="-14" width="8" height="24" rx="4"/>
          </g>
          <animateTransform attributeName="transform" type="rotate" values="-14;14;-14" dur="1s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1"/>
        </g>
      </g>
      <path d="M24 30 q6 -6 12 0 M76 30 q6 -6 12 0" stroke="${P.cyan}" stroke-width="3.4" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "bounce-happy",
    animated: true,
    keywords: "happy bounce excited yay joyful animate",
    body: `${gradDef("g", P.amber, "#D98324")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <g transform="translate(0 0)">
        <g>
          <circle cx="56" cy="52" r="27" fill="${P.ink}"/>
          ${happyFace()}
          <animateTransform attributeName="transform" type="translate" values="0 8;0 -8;0 8" dur="1.15s" repeatCount="indefinite" calcMode="spline" keySplines="0.3 0 0.2 1;0.3 0 0.2 1"/>
        </g>
      </g>`,
  },
  {
    id: "confetti-pop",
    animated: true,
    keywords: "confetti celebrate party pop congrats animate",
    body: `${gradDef("g", P.iris, "#9340C8")}
      <circle cx="56" cy="56" r="48" fill="url(#g)"/>
      <path d="M30 82 L50 48 a7 7 0 0 1 12 7 L40 88 a6 6 0 0 1 -10 -6 Z" fill="${P.amber}"/>
      <g>
        <rect x="64" y="26" width="7" height="7" rx="2" fill="${P.pink}">
          <animateTransform attributeName="transform" type="translate" values="0 0;-6 -14" dur="0.9s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="1;0" dur="0.9s" repeatCount="indefinite"/>
        </rect>
        <rect x="76" y="38" width="6" height="6" rx="2" fill="${P.cyan}">
          <animateTransform attributeName="transform" type="translate" values="0 0;8 -12" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="1;0" dur="0.8s" repeatCount="indefinite"/>
        </rect>
        <circle cx="60" cy="40" r="3.4" fill="${P.green}">
          <animateTransform attributeName="transform" type="translate" values="0 0;-12 -4" dur="1s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="1;0" dur="1s" repeatCount="indefinite"/>
        </circle>
        <circle cx="84" cy="30" r="3" fill="${P.ink}">
          <animateTransform attributeName="transform" type="translate" values="0 0;4 -12" dur="1.1s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="1;0" dur="1.1s" repeatCount="indefinite"/>
        </circle>
      </g>`,
  },
  {
    id: "shield-pulse",
    animated: true,
    keywords: "veto shield secure protected encrypted pulse animate",
    body: `${gradDef("g", P.cyan, P.iris)}
      <circle cx="56" cy="56" r="48" fill="${P.navy}"/>
      <circle cx="56" cy="56" r="40" fill="none" stroke="${P.irisBright}" stroke-width="2" opacity="0.6">
        <animate attributeName="r" values="36;48" dur="1.6s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.55;0" dur="1.6s" repeatCount="indefinite"/>
      </circle>
      <path d="M56 20 L88 32 v22 c0 19 -14 33 -32 42 C38 87 24 73 24 54 V32 Z" fill="url(#g)"/>
      <path d="M40 56 l11 11 l22 -24" stroke="${P.ink}" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  },
];

/** Doodle pack — ink-outline style on transparent background (sticker feel). */
const DOODLE_DEFS: Def[] = [
  {
    id: "doodle-heart",
    keywords: "heart love doodle cute",
    body: `${heartPath(56, 58, 3, "#F4586E", `stroke="#EDF1FC" stroke-width="4"`)}
      <path d="M40 42 q4 -6 9 -3" stroke="#EDF1FC" stroke-width="3.5" stroke-linecap="round" fill="none" opacity="0.85"/>`,
  },
  {
    id: "doodle-star",
    keywords: "star sparkle doodle cute",
    body: `<path d="M56 16 l11 22 24 3.6 -17 17 4 24 -22 -11.6 -22 11.6 4 -24 -17 -17 24 -3.6 Z" fill="#F6B852" stroke="#EDF1FC" stroke-width="4" stroke-linejoin="round"/>
      <path d="M44 38 q3 -5 8 -3" stroke="#EDF1FC" stroke-width="3" stroke-linecap="round" fill="none" opacity="0.85"/>`,
  },
  {
    id: "doodle-thumbs",
    keywords: "thumbs up like good doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" fill="#6C5CFF">
      <path d="M30 50 h14 v30 h-14 a5 5 0 0 1 -5 -5 v-20 a5 5 0 0 1 5 -5 Z"/>
      <path d="M44 80 v-26 c0 -3 1.5 -5 4 -7 l12 -11 c3 -2.6 8 -1.4 9.4 2.8 c1 3 -0.4 6.4 -3 8.2 l-6 4 h18 c3.4 0 6.4 3 5.6 6.6 l-4 18 c-0.6 2.8 -3 4.4 -5.8 4.4 Z"/>
    </g>`,
  },
  {
    id: "doodle-lol",
    keywords: "laughing lol funny doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round" fill="#F6B852">
      <circle cx="56" cy="56" r="36"/>
      <path d="M36 48 q7 -7 14 0 M62 48 q7 -7 14 0" fill="none" stroke="#EDF1FC" stroke-width="4"/>
      <path d="M38 60 Q56 84 74 60 Z" fill="#0D1322"/>
    </g>
    <path d="M18 52 q-5 10 0 16 q8 -5 5 -16 Z M94 52 q5 10 0 16 q-8 -5 -5 -16 Z" fill="#3FD8E4"/>`,
  },
  {
    id: "doodle-fire",
    keywords: "fire hot flame doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linejoin="round">
      <path d="M56 14 C68 30 82 40 82 60 a26 26 0 0 1 -52 0 c0 -13 9 -20 15 -29 c2 7 6 10 10 12 c-2 -10 -0.5 -19 1 -29 Z" fill="#F6B852"/>
      <path d="M56 48 c8 8 13 12 13 21 a13 13 0 0 1 -26 0 c0 -8 5 -12 13 -21 Z" fill="#F4586E" stroke-width="4"/>
    </g>`,
  },
  {
    id: "doodle-cat",
    keywords: "cat kitten doodle cute animal",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M34 44 l-4 -16 16 9 Z M78 44 l4 -16 -16 9 Z" fill="#8B7CFF"/>
      <ellipse cx="56" cy="60" rx="27" ry="24" fill="#8B7CFF"/>
      <path d="M36 58 h12 M76 58 h-12" stroke="#EDF1FC" stroke-width="3" fill="none"/>
      <circle cx="46" cy="56" r="4" fill="#3DDC97" stroke="none"/>
      <circle cx="66" cy="56" r="4" fill="#3DDC97" stroke="none"/>
      <path d="M56 64 l-3.4 4 h6.8 Z" fill="#F4586E" stroke="none"/>
      <path d="M49 71 q7 6 14 0" fill="none"/>
    </g>`,
  },
  {
    id: "doodle-ghost",
    keywords: "ghost boo spooky doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round">
      <path d="M32 86 v-28 a24 24 0 0 1 48 0 v28 l-8 -6 -8 6 -8 -6 -8 6 -8 -6 Z" fill="#EDF1FC"/>
      <circle cx="47" cy="52" r="4.6" fill="#0D1322" stroke="none"/>
      <circle cx="65" cy="52" r="4.6" fill="#0D1322" stroke="none"/>
      <ellipse cx="56" cy="66" rx="4.4" ry="5.4" fill="#0D1322" stroke="none"/>
    </g>
    <circle cx="28" cy="30" r="3" fill="#3FD8E4"/>
    <circle cx="84" cy="22" r="2.4" fill="#8B7CFF"/>`,
  },
  {
    id: "doodle-rocket",
    keywords: "rocket launch fast doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M56 14 c12 8 18 24 18 40 l-8 10 h-20 l-8 -10 c0 -16 6 -32 18 -40 Z" fill="#EDF1FC"/>
      <circle cx="56" cy="44" r="7.5" fill="#3FD8E4" stroke="#4B3BD6" stroke-width="3.5"/>
      <path d="M46 60 l-10 12 c6 2 8 6 8 10 Z M66 60 l10 12 c-6 2 -8 6 -8 10 Z" fill="#F4586E" stroke-width="4"/>
      <path d="M51 74 h10 c0 7 -2.5 12 -5 18 c-2.5 -6 -5 -11 -5 -18 Z" fill="#F6B852" stroke-width="3.5"/>
    </g>`,
  },
  {
    id: "doodle-coffee",
    keywords: "coffee cafe morning doodle",
    body: `<g stroke="#EDF1FC" stroke-width="4.5" stroke-linecap="round">
      <path d="M30 46 h42 v22 a14 14 0 0 1 -14 14 h-14 a14 14 0 0 1 -14 -14 Z" fill="#8A5A2B"/>
      <path d="M72 50 h5 a8 8 0 0 1 0 16 h-5" fill="none"/>
      <path d="M40 38 q3 -6 0 -11 M54 38 q3 -6 0 -11 M68 38 q3 -6 0 -11" fill="none" stroke="#DCD6FF" stroke-width="3.5"/>
    </g>`,
  },
  {
    id: "doodle-music",
    keywords: "music note jam song doodle",
    body: `<g fill="#3FD8E4" stroke="#EDF1FC" stroke-width="4">
      <ellipse cx="42" cy="78" rx="9" ry="7.5"/>
      <ellipse cx="74" cy="72" rx="9" ry="7.5"/>
      <path d="M51 76 v-38 l32 -6 v34" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
    <path d="M30 26 q4 -6 9 -2 M84 20 q4 -5 8 -1" stroke="#DCD6FF" stroke-width="3" stroke-linecap="round" fill="none"/>`,
  },
  {
    id: "doodle-zap",
    keywords: "lightning zap energy fast doodle",
    body: `<path d="M62 12 L34 58 h15 l-8 38 34 -52 h-18 l11 -32 Z" fill="#F6B852" stroke="#EDF1FC" stroke-width="4.5" stroke-linejoin="round"/>
      <circle cx="26" cy="30" r="3" fill="#3FD8E4"/>
      <circle cx="88" cy="74" r="3" fill="#3FD8E4"/>`,
  },
  {
    id: "doodle-rainbow",
    keywords: "rainbow colors pride doodle",
    body: `<g fill="none" stroke-linecap="round">
      <path d="M20 78 a36 36 0 0 1 72 0" stroke="#F4586E" stroke-width="8"/>
      <path d="M29 78 a27 27 0 0 1 54 0" stroke="#F6B852" stroke-width="8"/>
      <path d="M38 78 a18 18 0 0 1 36 0" stroke="#3DDC97" stroke-width="8"/>
      <path d="M47 78 a9 9 0 0 1 18 0" stroke="#6C5CFF" stroke-width="8"/>
      <path d="M14 84 h84" stroke="#EDF1FC" stroke-width="4" stroke-linecap="round"/>
    </g>
    <circle cx="86" cy="30" r="3.4" fill="#DCD6FF"/>
    <circle cx="24" cy="34" r="2.6" fill="#BDEDE0"/>`,
  },
];

export const BUILTIN_STICKERS: BuiltinSticker[] = [...CLASSIC_DEFS, ...DOODLE_DEFS].map((d) => ({
  id: d.id,
  animated: !!d.animated,
  keywords: d.keywords,
  uri: dataUri(svgDoc(d.body)),
}));

const STICKER_BY_PACK_AND_ID = new Map(
  BUILTIN_STICKERS.map((s) => {
    const pack = CLASSIC_DEFS.some((d) => d.id === s.id) ? STICKER_PACK_ID : STICKER_PACK_DOODLE;
    return [`${pack}/${s.id}`, s] as const;
  }),
);

export const STICKER_PACKS: StickerPack[] = [
  {
    id: STICKER_PACK_ID,
    label: "Classic",
    note: "Aurora badge art — bundled locally",
    stickers: STICKER_PACKS_STICKERS(CLASSIC_DEFS),
  },
  {
    id: STICKER_PACK_DOODLE,
    label: "Doodle",
    note: "Ink outlines — bundled locally",
    stickers: STICKER_PACKS_STICKERS(DOODLE_DEFS),
  },
];

function STICKER_PACKS_STICKERS(defs: Def[]): BuiltinSticker[] {
  return BUILTIN_STICKERS.filter((s) => defs.some((d) => d.id === s.id));
}

export function getBuiltinSticker(packId: string, id: string): BuiltinSticker | undefined {
  return STICKER_BY_PACK_AND_ID.get(`${packId}/${id}` as Parameters<typeof STICKER_BY_PACK_AND_ID.get>[0]);
}

export function getBuiltinStickerByRef(ref: string): BuiltinSticker | undefined {
  const [pack, id] = ref.split("/");
  if (!pack || !id) return undefined;
  return getBuiltinSticker(pack, id);
}

/**
 * Sealed wire body / label helpers — canonical forms live in @veto/protocol.
 */
export { stickerWireBody, stickerRefLabel } from "@veto/protocol";

export const STICKER_FEATURE_NOTE = STICKER_GIF_FEATURE_STATUS.gateMessage;
